﻿import React, { useState, useEffect, useId } from 'react';
import { Asset } from '../models/asset';
import { DataTemplates } from '../templates/data-templates';
import { CrudFunctions } from '../utilities/crud-functions';
import LogbookEntryComponent from './Common/LogBookEntryComponent';
import InstrumentSelectBoxTooltip from './InstrumentSelectBoxTooltip';
import InstrumentInformation from './InstrumentInformation';
import { BenchSheetTechnologyType } from '../models/enums';
import { FieldUpdate } from '../models/field-update';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

export interface Props {
    benchSheetId: string;
    isReadOnly: boolean;
    spotCheckBenchSheetId?: string;
    technology?: BenchSheetTechnologyType;
    title?: string;
    headerBgClass?: string;
    showHeader?: boolean;
    selectBoxTitle?: string;
    enableLogbook?: boolean;
    overrideInstrumentsUrl?: string;
}

export default function SecondaryBenchSheetInstrument(props: Props) {
    const [instruments, setInstruments] =
        useState<Asset[]>();

    const [instrumentId, setInstrumentId] =
        useState<string>();

    const [selectedInstrument, setSelectedInstrument] =
        useState<Asset>();

    const [logbookEntryDialogVisible, setLogbookEntryDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    const showHeader = props.showHeader ?? true;

    const enableLogbook = props.enableLogbook ?? true;

    // Create unique id on the Selectbox (for Tooltip's target) since parent component may
    // render this component more than once
    const targetId = `sb-${useId().replace(/:/g, '')}`;

    useEffect(() => {
        if (!dataLoaded)
            getAllData();
    }, [props.benchSheetId]);

    return (
        <div>
            {showHeader &&
                <div className={`lq-header pw-margin-top-05 ${props.headerBgClass ? props.headerBgClass : 'pw-bg-rb-700'}`}>
                    <div className="lq-header-title">{props.title ?? "Instruments"}</div>
                </div>
            }

            {dataLoaded && instruments &&
                <div className="pw-form-section">
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">{props.selectBoxTitle ?? "Instrument"}</div>
                            <SelectBox
                                elementAttr={{ id: targetId }}
                                readOnly={props.isReadOnly}
                                placeholder={'Select an Instrument'}
                                className="asset-selectbox"
                                height='2.1em'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={instruments}
                                onValueChanged={onInstrumentChanged}
                                valueExpr={'assetId'}
                                displayExpr={(item) => {
                                    return item ?
                                        `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}`
                                        : "";
                                }}
                                value={selectedInstrument?.assetId} />

                            <InstrumentSelectBoxTooltip target={`#${targetId}`} instrument={selectedInstrument} />
                        </div>

                        {enableLogbook &&
                            <div className="pw-row-button-08">
                                <Button text="Logbook" icon="edit" type="success"
                                    onClick={showLogbookEntryDialog} />
                            </div>
                        }

                        <InstrumentInformation instrument={selectedInstrument} />
                    </div>
                </div>
            }

            {logbookEntryDialogVisible &&
                <DialogPopup visible={logbookEntryDialogVisible}
                    onHiding={hideLogbookEntryDialog}
                    title="Logbook Entry"
                    width='80vw'
                    height='80vh'
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    resizeEnabled={false}>
                    <LogbookEntryComponent asset={selectedInstrument ? selectedInstrument : null}
                        benchSheetId={props.benchSheetId}
                        onClose={hideLogbookEntryDialog} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function findAndSetMatchingInstrument(allInstruments: Asset[], id: string) {
        const found = allInstruments.find(i => i.assetId === id);
        if (found) {
            setSelectedInstrument(found);
        }
    }

    async function onInstrumentChanged(e: any) {
        if (!dataLoaded)
            return;

        if (instruments && e.value) {
            if (selectedInstrument && selectedInstrument.assetId === e.value)
                return;
            else if (!selectedInstrument && !e.value)
                return;
            else {
                const foundInstrument = instruments.find(i => i.assetId === e.value);

                if (foundInstrument) {
                    setSelectedInstrument(foundInstrument);

                    const fieldUpdate: FieldUpdate = {
                        entity: "BenchSheets",
                        field: "instrumentId",
                        value: e.value,
                        rowId: props.benchSheetId,
                        parentId: props.spotCheckBenchSheetId
                    };

                    if (props.spotCheckBenchSheetId) {
                        await updateSpotCheckField(fieldUpdate)
                            .catch((res) => {
                                showMessage(res, ModalMessageType.Error, "Error Updating Instrument");
                            });
                    }
                    else {
                        await updateField(fieldUpdate)
                            .catch((res) => {
                                showMessage(res, ModalMessageType.Error, "Error Updating Instrument");
                            });
                    }
                }
            }
        }
    }

    async function getInstruments(): Promise<Asset[]> {
        try {
            const url = props.overrideInstrumentsUrl || `assets/assets?technologyType=${props.technology}`;
            const data = await CrudFunctions.getData(url);
            setInstruments(data);
            return (data);
        } catch (res) {
            showMessage(res, ModalMessageType.Error, "Error Retrieving Instruments");
            throw res;
        }
    }

    async function getInstrumentId(): Promise<string> {
        const url = props.spotCheckBenchSheetId ? `${props.benchSheetId}/${props.spotCheckBenchSheetId}` : props.benchSheetId;

        try {
            const data = await CrudFunctions.getData(`benchsheets/instrumentidbybenchsheetid/${url}`);
            setInstrumentId(data.value);
            return (data.value);
        } catch (res) {
            showMessage(res, ModalMessageType.Error, "Error Retrieving Instrument Id");
            throw res;
        }
    }
    // PUT functions

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/benchsheetfield", fieldUpdate);
    }

    async function updateSpotCheckField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/spotcheckbenchsheetfield", fieldUpdate);
    }

    async function getAllData() {
        try {
            const [allInstruments, idToMatch] = await Promise.all([
                getInstruments(),
                getInstrumentId()
            ]);
            findAndSetMatchingInstrument(allInstruments, idToMatch);
            setDataLoaded(true)
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Retrieving Instrument data.");
        }
    }

    function showLogbookEntryDialog() {
        setLogbookEntryDialogVisible(true);
    }

    function hideLogbookEntryDialog() {
        setLogbookEntryDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
